import { NetworkStatus, useQuery } from '@apollo/client';
import { ErrorRetry, ListSelectionStyled, SubListSelectionStyled } from '@bechtel/client-common';
import { GET_BLOCK_TYPES } from '@bechtel/client-gql';
import { Box, Checkbox, Set, SetDirection, SetSpacing, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { BlockType } from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { FieldArray, FieldProps } from 'formik';
import { findIndex, some } from 'lodash';
import React, { Fragment } from 'react';
import { BlockTypeMessages } from '~/components';

export const BlockTypeList: React.FC<FieldProps> = ({ form }) => {
  const { loading, error, data, refetch, networkStatus } = useQuery(GET_BLOCK_TYPES, {
    notifyOnNetworkStatusChange: true,
  });

  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading Block Type List. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && (
        <Box data-testid="success" flexDirection={{ sm: 'column' }}>
          <SubListSelectionStyled
            onClick={() => {
              if (form.values.blockTypes?.length !== data.blockTypes.length) {
                form.setFieldValue('blockTypes', data.blockTypes);
              } else {
                form.setFieldValue('blockTypes', []);
              }
            }}
          >
            <Box alignItems={{ sm: 'center' }}>
              <Box mr={{ sm: '500' }}>
                <Checkbox
                  checked={form.values.blockTypes?.length === data.blockTypes.length}
                  indeterminate={
                    form.values.blockTypes?.length > 0 && form.values.blockTypes?.length < data.blockTypes.length
                  }
                  readOnly
                />
              </Box>
              <Text size="400" weight="600" color="bechtel.grey.1000">
                <Trans>Block Type</Trans>
              </Text>
            </Box>
            {form.values.blockTypes && form.values.blockTypes.length > 0 && (
              <Text size="400" color="bechtel.grey.1000">
                {form.values.blockTypes.length} <Trans>Selected</Trans>
              </Text>
            )}
          </SubListSelectionStyled>
          <FieldArray name="blockTypes">
            {(arrayHelpers) => {
              return (
                <>
                  {data.blockTypes &&
                    data.blockTypes.map((blockType: BlockType) => {
                      return (
                        <ListSelectionStyled
                          key={blockType.id}
                          data-testid={`block-card-search-canvas-block-types-${blockType.id}`}
                          onClick={() => {
                            const selected = findIndex(form.values.blockTypes, (item: BlockType) => {
                              return item.id === blockType.id;
                            });
                            if (selected !== -1) {
                              arrayHelpers.remove(selected);
                            } else {
                              arrayHelpers.push(blockType);
                            }
                          }}
                        >
                          <Box mr={{ sm: '500' }}>
                            <Checkbox
                              checked={some(form.values.blockTypes, (item: BlockType) => {
                                return item.id === blockType.id;
                              })}
                              readOnly
                            />
                          </Box>
                          <Set spacing={SetSpacing.COMPACT} direction={SetDirection.COL}>
                            <Text size="400" color="bechtel.grey.1000">
                              <Trans id={BlockTypeMessages[blockType.title].id} />
                            </Text>
                          </Set>
                        </ListSelectionStyled>
                      );
                    })}
                </>
              );
            }}
          </FieldArray>
        </Box>
      )}
    </Fragment>
  );
};
